import { HealthLocationList } from '@/components/health/HealthLocationList';
import type { Attraction } from '@/config/objects/attractions';
import { useApiClient } from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

interface AttractionsListResponse {
  success: boolean;
  data: Attraction[];
}

const QUERY_KEY = (id: string) => ['GreaterAreaAttractions', id] as const;

/**
 * Health > Location for a greater area: lists every attraction in this
 * area whose coordinates haven't been externally verified.
 *
 * Uses a dedicated query key so refetching after a save invalidates this
 * scope without disturbing the unrelated paginated AttractionsList that
 * powers the regular Attractions tab.
 */
export function GreaterAreaHealthLocationTab() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<AttractionsListResponse>({
    queryKey: id ? QUERY_KEY(id) : ['GreaterAreaAttractions', 'noop'],
    enabled: Boolean(id),
    queryFn: () =>
      apiClient.get<AttractionsListResponse>(`/greater-areas/${id}/attractions?limit=500`),
  });

  if (!id) return null;

  return (
    <HealthLocationList
      attractions={data?.data}
      isLoading={isLoading}
      onAttractionUpdated={() => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY(id) });
        queryClient.invalidateQueries({ queryKey: ['Attraction'] });
      }}
    />
  );
}
